import React, { useCallback } from 'react';
import { NodeViewProps } from '@tiptap/core';
import { NodeViewWrapper } from '@tiptap/react';
import FigmaEmbedCard from '../figmaEmbedCard';
import { getFigmaPreviewUrl } from '../../services/taskServices';
import { FigmaEmbedAttributesInterface } from '../../interfaces/tiptapInterface';
import createEmbed from './createEmbed';

export interface FigmaEmbedOptions {
  HTMLAttributes?: FigmaEmbedAttributesInterface;
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    figmaEmbed: {
      setFigmaEmbed: (options: {
        frameNames: string;
        projectName: string;
        filePreviewUrl: string;
        url: string;
        isLoading?: boolean;
        nodeId?: string;
      }) => ReturnType;
    };
  }
}

// React NodeView Component using FigmaEmbedCard
const FigmaEmbedComponent: React.FC<NodeViewProps> = ({ node, updateAttributes }) => {
  const { frameNames, projectName, filePreviewUrl, url, isLoading, figmaLoading } = node.attrs;
  const effectiveFigmaLoading = !!(figmaLoading || isLoading);

  const extractFigmaUrlData = useCallback((figmaUrl: string) => {
    const u = new URL(figmaUrl);
    return { fileId: u.pathname?.split('/')[2], nodes: u.searchParams.get('node-id') };
  }, []);

  const handleRefresh = useCallback(
    async (figmaURL: string) => {
      try {
        // Only spin the refresh icon, do not show full loading card
        updateAttributes({ figmaLoading: true });
        const { fileId, nodes } = extractFigmaUrlData(figmaURL);
        const data = await getFigmaPreviewUrl({ fileId, nodes });
        if (data && typeof data === 'object' && 'filePreviewUrl' in data) {
          updateAttributes({
            frameNames: data.frameNames || frameNames,
            projectName: data.projectName || projectName,
            filePreviewUrl: data.filePreviewUrl,
            figmaLoading: false
          });
        } else {
          updateAttributes({ figmaLoading: false });
        }
      } catch (e) {
        updateAttributes({ figmaLoading: false });
      }
    },
    [extractFigmaUrlData, frameNames, projectName, updateAttributes]
  );

  return (
    <NodeViewWrapper className='figma-embed-wrapper'>
      <FigmaEmbedCard
        node={{ attribs: {} }}
        frameNames={frameNames}
        projectName={projectName}
        filePreviewUrl={filePreviewUrl}
        url={url}
        isLoading={false}
        figmaLoading={effectiveFigmaLoading}
        onClickRefreshFigma={handleRefresh}
      />
    </NodeViewWrapper>
  );
};

export const FigmaEmbed = createEmbed({
  name: 'figmaEmbed',
  tag: 'div.figma-main-div',
  component: FigmaEmbedComponent,
  draggable: true,
  attributes: {
    frameNames: { default: '' },
    projectName: { default: '' },
    filePreviewUrl: { default: '' },
    url: { default: '' },
    isLoading: { default: false },
    figmaLoading: { default: false },
    nodeId: { default: '' }
  },
  parseHTMLGetAttrs: (dom: HTMLElement) => ({
    frameNames: dom.querySelector('.figma-description')?.textContent || dom.getAttribute('frameNames') || '',
    projectName: dom.querySelector('.figma-filename')?.textContent || dom.getAttribute('projectName') || '',
    filePreviewUrl:
      dom.querySelector('.figma-preview-image')?.getAttribute('src') || dom.getAttribute('filePreviewUrl') || '',
    url: dom.querySelector('.figma-link-div')?.getAttribute('href') || dom.getAttribute('url') || '',
    isLoading: dom.classList.contains('figma-loading-state') || dom.getAttribute('isLoading') === 'true',
    nodeId: dom.getAttribute('data-node-id') || ''
  }),
  renderHTMLAttributes: (HTMLAttributes) => {
    const { frameNames, projectName, filePreviewUrl, url, isLoading, nodeId } = HTMLAttributes;
    return {
      class: isLoading ? 'figma-main-div figma-loading-state' : 'figma-main-div',
      'data-node-id': nodeId,
      frameNames,
      projectName,
      filePreviewUrl,
      url,
      isLoading: String(!!isLoading)
    };
  }
});
